import { FC, useMemo } from "react";
import { MDSButton, MDSButtonVariant, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { RecallPermissionKind } from "@/modules/electron/types";
import {
  ConfigurationList,
  ConfigurationListItem,
  ConfigurationListItemKind,
  ConfigurationPermissionStatus,
} from "@/components/configuration";
import {
  StepTitle,
  StepContent,
  FooterContainer,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.styles";
import { OnboardingStepper } from "@/components/onboarding/components/welcome-wizard/shared";

type Props = {
  onNext: () => void;
  onSkip: () => void;
  showFinish: boolean;
  restartRequired: boolean;
  resolveConfigurationStatus: ({
    kind,
  }: {
    kind: RecallPermissionKind;
  }) => ConfigurationPermissionStatus;
  handleEnablePermission: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  handleOpenPermissionPane: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
};

export const OnboardingVoicePermissionsScreen: FC<Props> = ({
  onNext,
  onSkip,
  showFinish,
  restartRequired,
  resolveConfigurationStatus,
  handleEnablePermission,
  handleOpenPermissionPane,
}) => {
  const microphoneStatus = resolveConfigurationStatus({ kind: RecallPermissionKind.Microphone });
  const accessibilityStatus = resolveConfigurationStatus({
    kind: RecallPermissionKind.Accessibility,
  });

  const systemAudioStatus = resolveConfigurationStatus({
    kind: RecallPermissionKind.SystemAudio,
  });

  const isMicrophoneGranted = microphoneStatus === ConfigurationPermissionStatus.Granted;
  const isAccessibilityGranted = accessibilityStatus === ConfigurationPermissionStatus.Granted;
  const isSystemAudioGranted = systemAudioStatus === ConfigurationPermissionStatus.Granted;

  const isMicrophoneLoading = microphoneStatus === ConfigurationPermissionStatus.Loading;
  const isAccessibilityLoading = accessibilityStatus === ConfigurationPermissionStatus.Loading;
  const isSystemAudioLoading = systemAudioStatus === ConfigurationPermissionStatus.Loading;

  const isAnyLoading = isMicrophoneLoading || isAccessibilityLoading || isSystemAudioLoading;
  const allPermissionsGranted =
    isMicrophoneGranted && isAccessibilityGranted && isSystemAudioGranted;

  /**
   * We let the user proceed as soon as they've requested all permissions.
   */
  const canProceed = (() => {
    if (isAnyLoading) {
      return false;
    }

    if (!isMicrophoneGranted) {
      return false;
    }

    if (!isAccessibilityGranted) {
      return false;
    }

    if (restartRequired) {
      return true;
    }

    return allPermissionsGranted;
  })();

  const items = useMemo<ConfigurationListItem[]>(() => {
    const itemList: ConfigurationListItem[] = [
      {
        kind: ConfigurationListItemKind.Permission,
        id: "microphone",
        title: "Microphone",
        description: "Mem relies on your microphone to capture your voice",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.Microphone }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.Microphone,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.Microphone,
          }),
      },
      {
        kind: ConfigurationListItemKind.Divider,
        id: "divider-accessibility",
      },
      {
        kind: ConfigurationListItemKind.Permission,
        id: "accessibility",
        title: "Accessibility",
        description: "Mem relies on accessibility features to start voice recording on desktop",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.Accessibility }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.Accessibility,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.Accessibility,
          }),
      },
      {
        kind: ConfigurationListItemKind.Divider,
        id: "divider-screen-recording",
      },
      {
        kind: ConfigurationListItemKind.Permission,
        id: "system-audio-recording",
        title: "System audio recording only",
        description:
          "Mem relies on this permission to capture other people's voices during meetings",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.SystemAudio }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.SystemAudio,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.SystemAudio,
          }),
      },
    ];

    return itemList;
  }, [handleEnablePermission, handleOpenPermissionPane, resolveConfigurationStatus]);

  return (
    <WizardContentWrapper>
      <StepTitle>Set up Voice Mode</StepTitle>

      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          Mem can record and create notes from meeting audio, podcasts, or simply your own voice.
          Grant the required permissions in your system settings.
        </MDSText>

        <ConfigurationList items={items} />
      </StepContent>

      <FooterContainer align="flex-end">
        <OnboardingStepper />
        {!allPermissionsGranted && (
          <MDSButton variant={MDSButtonVariant.Transparent} onClick={onSkip}>
            I don&apos;t want to use Voice Mode
          </MDSButton>
        )}
        <MDSButton variant={MDSButtonVariant.Contrast} onClick={onNext} isDisabled={!canProceed}>
          {showFinish ? "Finish" : "Next"}
        </MDSButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
};
